import { useState, useMemo, useEffect, type FormEvent } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow, format, isToday, isYesterday, isThisWeek } from 'date-fns'
import {
  ChevronLeft,
  Pencil,
  Trash2,
  Plus,
  Phone,
  MessageSquare,
  Mail,
  Users,
  Coffee,
  UtensilsCrossed,
  Video,
  MoreHorizontal,
  MoreVertical,
  MapPin,
  Cake,
  Heart,
  Clock,
  StickyNote,
  CheckSquare,
  Square,
  ExternalLink,
  Gift,
  ShoppingCart,
  CalendarDays,
  Tag,
  RefreshCw,
  Globe,
  Link as LinkIcon,
  FileText,
  Search,
  Bell,
  Mic,
  CheckCircle2,
  Share2,
  Sparkles,
  Target,
  PartyPopper,
  Church,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  computeHealthScore,
  qualityToNumber,
  HEALTH_COLORS,
  type HealthScore,
} from '@/lib/health-score'
import { Button, buttonVariants } from '@/components/ui/button'
import { useContact, useDeleteContact, useUpdateContact } from '@/hooks/use-contacts'
import { useInteractions, useDeleteInteraction } from '@/hooks/use-interactions'
import { useContactTodos, useCreateTodo, useUpdateTodo, useDeleteTodo } from '@/hooks/use-todos'
import { useContactGifts, useCreateGift, useUpdateGift, useDeleteGift } from '@/hooks/use-gifts'
import { useContactVoiceNotes, useDeleteVoiceNote } from '@/hooks/use-voice-notes'
import { useContactReminders } from '@/hooks/use-reminders'
import { useTimeline } from '@/hooks/use-timeline'
import {
  useContactNotes,
  useCreateContactNote,
  useUpdateContactNote,
  useDeleteContactNote,
} from '@/hooks/use-contact-notes'
import { QuickLogChips } from '@/components/QuickLogChips'
import { ShareContactDialog } from '@/components/ShareContactDialog'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { TemplateManager } from '@/components/TemplateManager'
import { VoiceNoteRecorder } from '@/components/VoiceNoteRecorder'
import { EmptyState } from '@/components/EmptyState'
import { AiInsightsCard } from '@/components/AiInsightsCard'
import { useContactTargetForContact } from '@/hooks/use-contact-targets'
import { EnrichmentDialog } from '@/components/EnrichmentDialog'
import { useStartRescrape, useRescrapeJobStatus, useProfileSnapshots } from '@/hooks/use-rescrape'
import { useContactRelationships } from '@/hooks/use-contact-relationships'
import { StreakBadge } from '@/components/StreakBadge'
import { useContactStreak } from '@/hooks/use-gamification'
import { useNameDay } from '@/hooks/use-upcoming-events'
import { UpgradePrompt } from '@/components/UpgradePrompt'
import { SummaryButton } from '@/components/SummaryModal'
import { EditInteractionModal } from '@/components/EditInteractionModal'
import { useSession } from '@/hooks/use-auth'
import { canAccess, type Role } from '@/lib/features'

const typeIcons: Record<string, typeof Phone> = {
  call: Phone,
  meeting: Users,
  text: MessageSquare,
  email: Mail,
  coffee: Coffee,
  dinner: UtensilsCrossed,
  video_call: Video,
  other: MoreHorizontal,
}

const qualityColors: Record<string, string> = {
  great: 'tag-chartreuse',
  good: 'tag-lilac',
  neutral: 'bg-muted text-muted-foreground',
  poor: 'tag-coral',
}

type Tab = 'overview' | 'timeline' | 'notes' | 'todos' | 'gifts' | 'voice'

function getInitials(firstName: string, lastName: string | null): string {
  const first = firstName.charAt(0).toUpperCase()
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return first + last
}

function formatFrequency(freq: string): string {
  const map: Record<string, string> = {
    weekly: 'Weekly',
    biweekly: 'Every 2 weeks',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    none: 'No reminder',
  }
  return map[freq] ?? freq
}

export default function ContactDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showActionMenu, setShowActionMenu] = useState(false)
  const [showTemplateManager, setShowTemplateManager] = useState(false)
  const [showFrequencyPicker, setShowFrequencyPicker] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [showIntroDialog, setShowIntroDialog] = useState(false)
  const [showEnrichDialog, setShowEnrichDialog] = useState(false)
  const [editingInteraction, setEditingInteraction] = useState<Interaction | null>(null)
  const [timelinePage, setTimelinePage] = useState(1)
  const [rescrapeJobId, setRescrapeJobId] = useState<string | null>(null)

  // Reset rescrape state when switching contacts
  useEffect(() => {
    setRescrapeJobId(null)
  }, [id])

  const { data: session } = useSession()
  const userRole = (session?.user?.role ?? 'free') as Role

  const { data: contact, isLoading } = useContact(id)
  const deleteContact = useDeleteContact()
  const updateContact = useUpdateContact()
  const { data: interactionsData } = useInteractions(id, timelinePage)
  const { data: todos } = useContactTodos(id)
  const { data: gifts } = useContactGifts(id)
  const { data: voiceNotes, refetch: refetchVoiceNotes } = useContactVoiceNotes(id)
  const deleteVoiceNote = useDeleteVoiceNote()
  useContactReminders(id)
  const { data: streakData } = useContactStreak(id)
  const { data: targetData } = useContactTargetForContact(id)
  const { data: nameDayData } = useNameDay(contact?.firstName)

  const [notesPage, setNotesPage] = useState(1)
  const [notesSearch, setNotesSearch] = useState('')
  const { data: notesData } = useContactNotes(id, notesPage, notesSearch || undefined)
  const createNote = useCreateContactNote()
  const updateNote = useUpdateContactNote()
  const deleteNote = useDeleteContactNote()

  const deleteInteraction = useDeleteInteraction()
  const createTodo = useCreateTodo()
  const updateTodo = useUpdateTodo()
  const deleteTodo = useDeleteTodo()
  const createGift = useCreateGift()
  const updateGift = useUpdateGift()
  const deleteGift = useDeleteGift()

  const startRescrape = useStartRescrape()
  const { data: rescrapeJobData } = useRescrapeJobStatus(id, rescrapeJobId ?? undefined)

  const [newTodoTitle, setNewTodoTitle] = useState('')
  const [newGiftTitle, setNewGiftTitle] = useState('')
  const [newGiftPrice, setNewGiftPrice] = useState('')

  const healthScore = useMemo((): HealthScore | null => {
    if (!contact || !interactionsData) return null
    const allInteractions = interactionsData.items
    // eslint-disable-next-line react-hooks/purity
    const now = Date.now()
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000
    const sixtyDaysAgo = now - 60 * 24 * 60 * 60 * 1000

    const recentInteractions = allInteractions.filter(
      (i) => new Date(i.occurredAt).getTime() >= thirtyDaysAgo,
    )
    const previousInteractions = allInteractions.filter((i) => {
      const t = new Date(i.occurredAt).getTime()
      return t >= sixtyDaysAgo && t < thirtyDaysAgo
    })

    const qualityValues = recentInteractions
      .map((i) => qualityToNumber(i.quality))
      .filter((v): v is number => v !== null)

    return computeHealthScore({
      lastContactedAt: contact.lastContactedAt,
      contactFrequency: contact.contactFrequency,
      interactionCount: interactionsData.total,
      interactionTrend: recentInteractions.length - previousInteractions.length,
      averageQuality:
        qualityValues.length > 0
          ? qualityValues.reduce((a, b) => a + b, 0) / qualityValues.length
          : null,
      reminderCompletionRate: null, // Not available per-contact in V1
    })
  }, [contact, interactionsData])

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-6 flex items-center gap-3">
          <div className="bg-surface-container-high h-8 w-8 animate-pulse rounded-lg" />
          <div className="bg-surface-container-high h-6 w-32 animate-pulse rounded" />
        </div>
        <div className="flex flex-col items-center gap-3">
          <div className="bg-surface-container-high size-20 animate-pulse rounded-full" />
          <div className="bg-surface-container-high h-6 w-40 animate-pulse rounded" />
          <div className="bg-surface-container-high h-4 w-28 animate-pulse rounded" />
        </div>
        <div className="mt-6 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface-container-high h-12 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-4">
        <p className="text-on-surface-variant text-lg">Contact not found</p>
        <Link to="/contacts" className="text-primary mt-2 text-sm underline">
          Back to contacts
        </Link>
      </div>
    )
  }

  const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ')

  function handleDelete() {
    if (!id) return
    navigate('/contacts', { replace: true })
    deleteContact.mutate(id)
  }

  function handleAddTodo(e: FormEvent) {
    e.preventDefault()
    const title = newTodoTitle.trim()
    if (!title || !id) return
    createTodo.mutate({ contactId: id, title }, { onSuccess: () => setNewTodoTitle('') })
  }

  function handleAddGift(e: FormEvent) {
    e.preventDefault()
    const title = newGiftTitle.trim()
    if (!title || !id) return
    createGift.mutate(
      { contactId: id, title, price: newGiftPrice.trim() || undefined },
      {
        onSuccess: () => {
          setNewGiftTitle('')
          setNewGiftPrice('')
        },
      },
    )
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'notes', label: 'Notes' },
    { key: 'todos', label: 'Todos' },
    { key: 'gifts', label: 'Gifts' },
    { key: 'voice', label: 'Voice' },
  ]

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="border-outline-variant/20 bg-card border-b px-6 pt-3 pb-4">
        {/* Top bar */}
        <div className="mb-4 flex items-center justify-between">
          <Link
            to="/contacts"
            className="text-on-surface-variant hover:text-foreground inline-flex items-center gap-1 text-sm transition-colors md:hidden"
          >
            <ChevronLeft className="size-4" />
            {t('contacts.title', 'Contacts')}
          </Link>
          <div className="relative ml-auto flex items-center gap-1">
            <Link
              to={`/contacts/${id}/interaction`}
              className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }))}
            >
              <Plus className="size-4" />
            </Link>
            <Link
              to={`/contacts/${id}/edit`}
              className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }))}
            >
              <Pencil className="size-4" />
            </Link>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setShowActionMenu(!showActionMenu)}
            >
              <MoreVertical className="size-4" />
            </Button>

            {showActionMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowActionMenu(false)} />
                <div className="border-outline-variant/20 bg-surface-container-highest/90 absolute top-full right-0 z-50 mt-1 w-48 rounded-xl py-1 shadow-lg backdrop-blur-xl">
                  <button
                    className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-sm"
                    onClick={() => {
                      setShowActionMenu(false)
                      setShowFrequencyPicker(true)
                    }}
                  >
                    <RefreshCw className="size-4" />
                    Set contact frequency
                  </button>
                  <Link
                    to={`/contacts/${id}/edit`}
                    className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-sm"
                    onClick={() => setShowActionMenu(false)}
                  >
                    <Pencil className="size-4" />
                    Edit contact
                  </Link>
                  <button
                    className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-sm"
                    onClick={() => {
                      setShowActionMenu(false)
                      setShowShareDialog(true)
                    }}
                  >
                    <Share2 className="size-4" />
                    {t('sharing.shareContact', 'Share contact')}
                  </button>
                  <button
                    className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-sm"
                    onClick={() => {
                      setShowActionMenu(false)
                      setShowIntroDialog(true)
                    }}
                  >
                    <Sparkles className="size-4" />
                    {t('sharing.introduce', 'Draft introduction')}
                  </button>
                  <button
                    className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-sm"
                    onClick={() => {
                      setShowActionMenu(false)
                      setShowEnrichDialog(true)
                    }}
                  >
                    <Sparkles className="size-4" />
                    Enrich contact
                  </button>
                  {contact?.links?.linkedin && (
                    <button
                      className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-sm"
                      disabled={startRescrape.isPending || rescrapeJobData?.status === 'running'}
                      onClick={() => {
                        setShowActionMenu(false)
                        startRescrape.mutate(id!, {
                          onSuccess: (data) => setRescrapeJobId(data.jobId),
                        })
                      }}
                    >
                      <RefreshCw
                        className={cn(
                          'size-4',
                          (startRescrape.isPending || rescrapeJobData?.status === 'running') &&
                            'animate-spin',
                        )}
                      />
                      {rescrapeJobData?.status === 'running'
                        ? 'Rescraping...'
                        : 'Rescrape LinkedIn'}
                    </button>
                  )}
                  <button
                    className="text-destructive hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-sm"
                    onClick={() => {
                      setShowActionMenu(false)
                      setShowDeleteConfirm(true)
                    }}
                  >
                    <Trash2 className="size-4" />
                    Delete contact
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Avatar and info */}
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="relative">
            {contact.avatarUrl ? (
              <img
                src={contact.avatarUrl}
                alt={fullName}
                className="size-20 rounded-full object-cover"
              />
            ) : (
              <div className="bg-primary/10 text-primary flex size-20 items-center justify-center rounded-full text-2xl font-semibold">
                {getInitials(contact.firstName, contact.lastName)}
              </div>
            )}
            {(startRescrape.isPending || rescrapeJobData?.status === 'running') && (
              <div className="bg-background/60 absolute inset-0 flex items-center justify-center rounded-full">
                <RefreshCw className="text-primary size-6 animate-spin" />
              </div>
            )}
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold">{fullName}</h1>
            {(contact.jobTitle || contact.company) && (
              <p className="text-on-surface-variant text-sm">
                {[contact.jobTitle, contact.company].filter(Boolean).join(' at ')}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            {healthScore && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                  HEALTH_COLORS[healthScore.level].bg,
                  HEALTH_COLORS[healthScore.level].text,
                )}
              >
                <span
                  className={cn(
                    'inline-block size-1.5 rounded-full',
                    HEALTH_COLORS[healthScore.level].dot,
                  )}
                />
                {healthScore.level === 'new'
                  ? t('health.new', 'New')
                  : `${healthScore.score} — ${t(`health.${healthScore.level}`, healthScore.level)}`}
              </span>
            )}
            {streakData && streakData.currentStreak > 0 && (
              <StreakBadge streak={streakData.currentStreak} size="md" />
            )}
            {contact.lastContactedAt && (
              <span className="bg-surface-container-high text-on-surface-variant rounded-full px-2.5 py-0.5 text-xs">
                Last contacted{' '}
                {formatDistanceToNow(new Date(contact.lastContactedAt), { addSuffix: true })}
              </span>
            )}
            {contact.contactFrequency && contact.contactFrequency !== 'none' && (
              <span className="bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-medium">
                <RefreshCw className="mr-1 inline size-3" />
                {formatFrequency(contact.contactFrequency)}
              </span>
            )}
            {targetData && (
              <Link
                to="/targets"
                className="tag-coral inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
              >
                <Target className="size-3" />
                Target &middot; {targetData.status}
              </Link>
            )}
            {nameDayData?.isToday && (
              <span className="bg-lilac-soft text-lilac-foreground dark:text-lilac inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium">
                <PartyPopper className="size-3" />
                {t('upcoming.nameDayToday', "It's their name day — wish them!")}
              </span>
            )}
          </div>

          {/* Quick actions */}
          <div className="mt-1 flex items-center gap-2">
            {contact.phone && (
              <a
                href={`tel:${contact.phone}`}
                className={cn(buttonVariants({ variant: 'outline', size: 'icon' }))}
              >
                <Phone className="size-4" />
              </a>
            )}
            {contact.phone && (
              <a
                href={`sms:${contact.phone}`}
                className={cn(buttonVariants({ variant: 'outline', size: 'icon' }))}
              >
                <MessageSquare className="size-4" />
              </a>
            )}
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className={cn(buttonVariants({ variant: 'outline', size: 'icon' }))}
              >
                <Mail className="size-4" />
              </a>
            )}
          </div>
        </div>

        {/* Quick-log template chips */}
        {id && (
          <div className="mt-3 px-2">
            <QuickLogChips contactId={id} onManageTemplates={() => setShowTemplateManager(true)} />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-outline-variant/20 bg-card flex scrollbar-none overflow-x-auto border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors',
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'text-on-surface-variant hover:text-foreground border-transparent',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && <OverviewTab contact={contact} userRole={userRole} />}
        {activeTab === 'timeline' && (
          <TimelineTab
            contactId={id!}
            page={timelinePage}
            onPageChange={setTimelinePage}
            onEditInteraction={(interaction) => setEditingInteraction(interaction)}
            onDeleteInteraction={(interactionId) => deleteInteraction.mutate(interactionId)}
            userRole={userRole}
          />
        )}
        {activeTab === 'notes' && id && (
          <NotesTab
            notes={notesData?.items ?? []}
            total={notesData?.total ?? 0}
            page={notesPage}
            onPageChange={setNotesPage}
            search={notesSearch}
            onSearchChange={setNotesSearch}
            onCreate={(content) => createNote.mutate({ contactId: id, content })}
            isCreating={createNote.isPending}
            onUpdate={(noteId, content) => updateNote.mutate({ id: noteId, content })}
            onDelete={(noteId) => deleteNote.mutate(noteId)}
          />
        )}
        {activeTab === 'todos' && (
          <TodosTab
            todos={todos ?? []}
            newTitle={newTodoTitle}
            onNewTitleChange={setNewTodoTitle}
            onAdd={handleAddTodo}
            isAdding={createTodo.isPending}
            onToggle={(todo) => updateTodo.mutate({ id: todo.id, completed: !todo.completed })}
            onDelete={(todoId) => deleteTodo.mutate(todoId)}
          />
        )}
        {activeTab === 'gifts' && (
          <GiftsTab
            gifts={gifts ?? []}
            newTitle={newGiftTitle}
            newPrice={newGiftPrice}
            onNewTitleChange={setNewGiftTitle}
            onNewPriceChange={setNewGiftPrice}
            onAdd={handleAddGift}
            isAdding={createGift.isPending}
            onTogglePurchased={(gift) =>
              updateGift.mutate({ id: gift.id, purchased: !gift.purchased })
            }
            onDelete={(giftId) => deleteGift.mutate(giftId)}
          />
        )}
        {activeTab === 'voice' && id && (
          <VoiceNotesTab
            contactId={id}
            voiceNotes={voiceNotes ?? []}
            onSaved={() => refetchVoiceNotes()}
            onDelete={(noteId) => deleteVoiceNote.mutate(noteId)}
            userRole={userRole}
          />
        )}
      </div>

      {/* Frequency picker dialog */}
      {showFrequencyPicker && contact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface-container-low w-full max-w-sm rounded-xl p-6 shadow-lg">
            <h2 className="text-lg font-semibold">Contact frequency</h2>
            <p className="text-on-surface-variant mt-1 text-sm">
              How often do you want to stay in touch?
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {(['weekly', 'biweekly', 'monthly', 'quarterly', 'yearly', 'none'] as const).map(
                (freq) => (
                  <button
                    key={freq}
                    className={cn(
                      'border-outline-variant/30 rounded-xl border px-3 py-2 text-sm transition-colors',
                      contact.contactFrequency === freq
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'hover:bg-surface-container-high',
                    )}
                    onClick={() => {
                      if (id) {
                        updateContact.mutate({ id, contact_frequency: freq })
                      }
                      setShowFrequencyPicker(false)
                    }}
                  >
                    {formatFrequency(freq)}
                  </button>
                ),
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setShowFrequencyPicker(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template manager dialog */}
      <TemplateManager open={showTemplateManager} onClose={() => setShowTemplateManager(false)} />

      {/* Share contact dialog */}
      {showShareDialog && contact && (
        <ShareContactDialog contact={contact} onClose={() => setShowShareDialog(false)} />
      )}

      {/* Introduction dialog */}
      {showIntroDialog && contact && (
        <IntroductionDialog contact={contact} onClose={() => setShowIntroDialog(false)} />
      )}

      {/* Enrichment dialog */}
      {showEnrichDialog && contact && (
        <EnrichmentDialog contact={contact} onClose={() => setShowEnrichDialog(false)} />
      )}

      {/* Edit interaction modal */}
      {editingInteraction && (
        <EditInteractionModal
          interaction={editingInteraction}
          onClose={() => setEditingInteraction(null)}
        />
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface-container-low w-full max-w-sm rounded-xl p-6 shadow-lg">
            <h2 className="text-lg font-semibold">Delete contact?</h2>
            <p className="text-on-surface-variant mt-2 text-sm">
              This will delete {fullName}. You can undo this action for a few seconds after
              deleting.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteContact.isPending}
              >
                {deleteContact.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Overview Tab                                                              */
/* -------------------------------------------------------------------------- */

import type {
  Contact,
  Interaction,
  ContactNote,
  VoiceNote,
  ContactTodo,
  ContactGift,
  Reminder,
  TimelineEntry,
  TimelineEntryKind,
} from '@/lib/types'

/* Social media brand icons (SVG) for platforms not in lucide */
function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.88 0 1.441 1.441 0 012.88 0z" />
    </svg>
  )
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
    </svg>
  )
}

function PinterestIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z" />
    </svg>
  )
}

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  )
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

function SnapchatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.922-.214.04-.012.06-.012.08-.012.16 0 .3.075.42.194.12.12.18.272.18.405 0 .24-.18.36-.313.436-.16.048-.33.084-.51.12-.21.048-.39.09-.57.144-.39.12-.59.27-.59.51 0 .075.015.15.045.225.24.57.615 1.11 1.065 1.545.195.195.42.375.66.525.105.06.21.135.3.21.18.135.24.33.24.465 0 .255-.165.42-.39.51-.33.135-.705.195-1.11.24-.105.015-.21.045-.315.075-.045.015-.105.06-.165.12-.06.075-.105.165-.12.255-.015.09-.03.18-.06.27-.045.15-.135.3-.405.405-.21.075-.465.12-.765.165-.165.03-.345.045-.54.06-.12.015-.255.03-.39.06-.24.045-.48.135-.72.225-.345.12-.735.27-1.215.42-.48.15-.96.225-1.44.225-.48 0-.975-.075-1.44-.225-.48-.15-.87-.3-1.215-.42-.24-.09-.48-.18-.72-.225-.135-.03-.27-.045-.39-.06-.195-.015-.375-.03-.54-.06-.3-.045-.555-.09-.765-.165-.27-.105-.36-.255-.405-.405-.03-.09-.045-.18-.06-.27-.015-.09-.06-.18-.12-.255-.06-.06-.12-.105-.165-.12-.105-.03-.21-.06-.315-.075-.405-.045-.78-.105-1.11-.24-.225-.09-.39-.255-.39-.51 0-.135.06-.33.24-.465.09-.075.195-.15.3-.21.24-.15.465-.33.66-.525.45-.435.825-.975 1.065-1.545.03-.075.045-.15.045-.225 0-.24-.2-.39-.59-.51-.18-.054-.36-.096-.57-.144-.18-.036-.35-.072-.51-.12-.135-.075-.315-.195-.315-.435 0-.135.06-.285.18-.405.12-.12.27-.195.42-.195.02 0 .04 0 .08.015.265.09.625.225.925.21.195 0 .33-.045.4-.09-.007-.165-.017-.33-.03-.51l-.002-.06c-.105-1.628-.23-3.654.3-4.847C7.86 1.07 11.216.793 12.206.793z" />
    </svg>
  )
}

const LINK_CONFIG: {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
}[] = [
  { key: 'website', label: 'Website', icon: Globe, color: 'text-on-surface-variant' },
  { key: 'linkedin', label: 'LinkedIn', icon: LinkedInIcon, color: 'text-[#0A66C2]' },
  { key: 'instagram', label: 'Instagram', icon: InstagramIcon, color: 'text-[#E4405F]' },
  { key: 'tiktok', label: 'TikTok', icon: TikTokIcon, color: 'text-foreground' },
  { key: 'x', label: 'X', icon: XIcon, color: 'text-foreground' },
  { key: 'github', label: 'GitHub', icon: GitHubIcon, color: 'text-foreground' },
  { key: 'youtube', label: 'YouTube', icon: YouTubeIcon, color: 'text-[#FF0000]' },
  { key: 'pinterest', label: 'Pinterest', icon: PinterestIcon, color: 'text-[#BD081C]' },
  { key: 'snapchat', label: 'Snapchat', icon: SnapchatIcon, color: 'text-[#FFFC00]' },
]

const RELATIONSHIP_TYPE_LABELS: Record<string, string> = {
  colleague: 'Colleague',
  friend: 'Friend',
  family: 'Family',
  acquaintance: 'Acquaintance',
  introduced_by: 'Introduced by',
}

function OverviewTab({ contact, userRole }: { contact: Contact; userRole: Role }) {
  const { data: relationships } = useContactRelationships(contact.id)
  const { data: snapshotsData } = useProfileSnapshots(contact.id)
  const { data: nameDayData } = useNameDay(contact.firstName)
  const { t } = useTranslation()
  const rows: { icon: typeof MapPin; label: string; value: string }[] = []

  if (contact.location) {
    rows.push({ icon: MapPin, label: 'Location', value: contact.location })
  }
  if (contact.phone) {
    rows.push({ icon: Phone, label: 'Phone', value: contact.phone })
  }
  if (contact.email) {
    rows.push({ icon: Mail, label: 'Email', value: contact.email })
  }
  if (contact.birthday) {
    rows.push({
      icon: Cake,
      label: 'Birthday',
      value: format(new Date(contact.birthday), 'MMMM d, yyyy'),
    })
  }
  if (contact.anniversary) {
    rows.push({
      icon: Heart,
      label: 'Anniversary',
      value: format(new Date(contact.anniversary), 'MMMM d, yyyy'),
    })
  }
  if (nameDayData?.matched && nameDayData.date) {
    const nameDayDate = format(new Date(nameDayData.date), 'MMMM d')
    const saint = nameDayData.names?.length ? nameDayData.names.join(', ') : ''
    rows.push({
      icon: Church,
      label: t('upcoming.nameDay', 'Name day'),
      value: saint ? `${nameDayDate} · ${saint}` : nameDayDate,
    })
  }
  rows.push({
    icon: Clock,
    label: 'Contact frequency',
    value: formatFrequency(contact.contactFrequency),
  })

  return (
    <div className="space-y-1">
      {/* AI Insights */}
      <div className="bg-surface-container-low mb-3 rounded-xl p-4">
        {canAccess('ai_insights', userRole) ? (
          <AiInsightsCard contactId={contact.id} />
        ) : (
          <UpgradePrompt feature={t('ai.insights')} />
        )}
      </div>

      {/* Circles */}
      {contact.circles && contact.circles.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg px-1 py-2.5">
          <Users className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-on-surface-variant text-xs">Circles</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {contact.circles.map((circle) => (
                <Link
                  key={circle.id}
                  to={`/contacts?circle_id=${circle.id}`}
                  className="hover:bg-surface-container-high inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                >
                  {circle.color && (
                    <span
                      className="inline-block size-2 rounded-full"
                      style={{ backgroundColor: circle.color }}
                    />
                  )}
                  {circle.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Labels */}
      {contact.labels && contact.labels.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg px-1 py-2.5">
          <Tag className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-on-surface-variant text-xs">Labels</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {contact.labels.map((label) => (
                <Link
                  key={label.id}
                  to={`/contacts?label_id=${label.id}`}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                  style={{
                    backgroundColor: label.color ? `${label.color}20` : undefined,
                    color: label.color ?? undefined,
                    border: `1px solid ${label.color ?? 'currentColor'}`,
                  }}
                >
                  {label.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Relationships */}
      {relationships && relationships.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg px-1 py-2.5">
          <Users className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-on-surface-variant text-xs">Known connections</p>
            <div className="mt-1 space-y-1.5">
              {relationships.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/contacts/${rel.relatedContactId}`}
                  className="hover:bg-surface-container-high flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors"
                >
                  <div className="bg-primary/10 text-primary flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                    {rel.relatedAvatarUrl ? (
                      <img
                        src={rel.relatedAvatarUrl}
                        alt=""
                        className="h-7 w-7 rounded-full object-cover"
                      />
                    ) : (
                      `${rel.relatedFirstName.charAt(0)}${rel.relatedLastName?.charAt(0) ?? ''}`.toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {rel.relatedFirstName} {rel.relatedLastName}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {RELATIONSHIP_TYPE_LABELS[rel.relationshipType] ?? rel.relationshipType}
                      {rel.strength > 1 && ` · Strength ${rel.strength}/5`}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Links */}
      {contact.links && Object.keys(contact.links).length > 0 && (
        <div className="flex items-start gap-3 rounded-lg px-1 py-2.5">
          <LinkIcon className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-on-surface-variant text-xs">Links</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {LINK_CONFIG.filter((lc) => contact.links?.[lc.key]).map((lc) => {
                const Icon = lc.icon
                return (
                  <a
                    key={lc.key}
                    href={contact.links![lc.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'hover:bg-surface-container-high inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      lc.color,
                    )}
                    title={lc.label}
                  >
                    <Icon className="size-3.5" />
                    {lc.label}
                  </a>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {rows.map((row) => {
        const Icon = row.icon
        return (
          <div key={row.label} className="flex items-start gap-3 rounded-lg px-1 py-2.5">
            <Icon className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-on-surface-variant text-xs">{row.label}</p>
              <p className="text-sm">{row.value}</p>
            </div>
          </div>
        )
      })}
      {contact.notes && (
        <div className="flex items-start gap-3 rounded-lg px-1 py-2.5">
          <StickyNote className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-on-surface-variant text-xs">Notes</p>
            <p className="text-sm whitespace-pre-wrap">{contact.notes}</p>
          </div>
        </div>
      )}
      {contact.foodPreferences &&
        (contact.foodPreferences.dietary.length > 0 ||
          contact.foodPreferences.allergies.length > 0 ||
          contact.foodPreferences.favorites.length > 0 ||
          contact.foodPreferences.notes) && (
          <div className="flex items-start gap-3 rounded-lg px-1 py-2.5">
            <UtensilsCrossed className="text-on-surface-variant mt-0.5 size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-on-surface-variant text-xs">
                {t('contactDetail.foodPreferences', 'Food & Dietary')}
              </p>
              <div className="mt-1 space-y-1.5">
                {contact.foodPreferences.dietary.length > 0 && (
                  <div>
                    <p className="text-on-surface-variant text-xs">
                      {t('contactDetail.dietary', 'Dietary')}
                    </p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {contact.foodPreferences.dietary.map((d) => (
                        <span
                          key={d}
                          className="bg-surface-container-high rounded-full px-2 py-0.5 text-xs font-medium"
                        >
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {contact.foodPreferences.allergies.length > 0 && (
                  <div>
                    <p className="text-on-surface-variant text-xs">
                      {t('contactDetail.allergies', 'Allergies')}
                    </p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {contact.foodPreferences.allergies.map((a) => (
                        <span
                          key={a}
                          className="bg-destructive/15 text-destructive rounded-full px-2 py-0.5 text-xs font-medium"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {contact.foodPreferences.favorites.length > 0 && (
                  <div>
                    <p className="text-on-surface-variant text-xs">
                      {t('contactDetail.favorites', 'Favorites')}
                    </p>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {contact.foodPreferences.favorites.map((f) => (
                        <span
                          key={f}
                          className="bg-surface-container-high rounded-full px-2 py-0.5 text-xs font-medium"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {contact.foodPreferences.notes && (
                  <p className="text-sm whitespace-pre-wrap">{contact.foodPreferences.notes}</p>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Profile Version History */}
      {snapshotsData && snapshotsData.items.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 px-1 py-2">
            <FileText className="text-on-surface-variant size-4" />
            <p className="text-on-surface-variant text-xs font-medium tracking-wide uppercase">
              Profile History
            </p>
          </div>
          <div className="space-y-2">
            {snapshotsData.items.map((snapshot) => {
              const sourceLabels: Record<string, string> = {
                manual_rescrape: 'Manual rescrape',
                auto_rescrape: 'Auto rescrape',
                enrichment: 'Enrichment',
                import: 'Import',
              }
              const data = snapshot.snapshotData as Record<string, unknown>
              const changedFields = Object.keys(data).filter(
                (k) => data[k] !== null && data[k] !== undefined,
              )
              return (
                <div key={snapshot.id} className="bg-surface-container-low rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="bg-surface-container-high rounded-full px-2 py-0.5 text-xs font-medium">
                      {sourceLabels[snapshot.source] ?? snapshot.source}
                    </span>
                    <span className="text-on-surface-variant text-xs">
                      {format(new Date(snapshot.createdAt), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                  {changedFields.length > 0 && (
                    <div className="text-on-surface-variant mt-1.5 flex flex-wrap gap-1">
                      {changedFields.slice(0, 5).map((f) => (
                        <span
                          key={f}
                          className="bg-surface-container-highest rounded px-1.5 py-0.5 text-[10px]"
                        >
                          {f}: {String(data[f]).slice(0, 30)}
                        </span>
                      ))}
                      {changedFields.length > 5 && (
                        <span className="text-[10px]">+{changedFields.length - 5} more</span>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Timeline Tab (Unified Activity Feed)                                      */
/* -------------------------------------------------------------------------- */

const TIMELINE_FILTERS: { key: TimelineEntryKind | 'all'; labelKey: string; fallback: string }[] = [
  { key: 'all', labelKey: 'timeline.filterAll', fallback: 'All' },
  { key: 'interaction', labelKey: 'timeline.filterInteractions', fallback: 'Interactions' },
  { key: 'note', labelKey: 'timeline.filterNotes', fallback: 'Notes' },
  { key: 'voice', labelKey: 'timeline.filterVoice', fallback: 'Voice' },
  { key: 'todo', labelKey: 'timeline.filterTodos', fallback: 'Todos' },
  { key: 'gift', labelKey: 'timeline.filterGifts', fallback: 'Gifts' },
  { key: 'reminder', labelKey: 'timeline.filterReminders', fallback: 'Reminders' },
  { key: 'email', labelKey: 'timeline.filterEmails', fallback: 'Emails' },
]

/** Group a date into a human-readable label for the timeline */
function getDateGroupLabel(dateStr: string, t: (key: string, fallback: string) => string): string {
  const d = new Date(dateStr)
  if (isToday(d)) return t('timeline.today', 'Today')
  if (isYesterday(d)) return t('timeline.yesterday', 'Yesterday')
  if (isThisWeek(d)) return t('timeline.thisWeek', 'This week')
  return format(d, 'MMMM yyyy')
}

/** Group timeline entries by date label, preserving order */
function groupEntriesByDate(
  entries: TimelineEntry[],
  t: (key: string, fallback: string) => string,
): { label: string; entries: TimelineEntry[] }[] {
  const groups: { label: string; entries: TimelineEntry[] }[] = []
  let currentLabel = ''
  for (const entry of entries) {
    const label = getDateGroupLabel(entry.date, t)
    if (label !== currentLabel) {
      groups.push({ label, entries: [] })
      currentLabel = label
    }
    groups[groups.length - 1].entries.push(entry)
  }
  return groups
}

function TimelineTab({
  contactId,
  page,
  onPageChange,
  onEditInteraction,
  onDeleteInteraction,
  userRole,
}: {
  contactId: string
  page: number
  onPageChange: (p: number) => void
  onEditInteraction: (interaction: Interaction) => void
  onDeleteInteraction: (id: string) => void
  userRole: Role
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [filter, setFilter] = useState<TimelineEntryKind | 'all'>('all')

  const { data: timelineData, isLoading, isError } = useTimeline(contactId, page, filter)

  const items = timelineData?.items ?? []
  const totalPages = timelineData?.totalPages ?? 1
  const currentPage = timelineData?.page ?? page

  // Group entries by date
  const dateGroups = useMemo(() => groupEntriesByDate(items, t), [items, t])

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="text-on-surface-variant text-sm">
          {t('timeline.error', 'Could not load timeline. Pull to refresh.')}
        </p>
      </div>
    )
  }

  if (!isLoading && items.length === 0 && filter === 'all') {
    return (
      <EmptyState
        icon={Clock}
        title={t('timeline.empty', 'No activity yet — log your first interaction')}
        description={t('timeline.emptyHint', 'Record your interactions to build a timeline.')}
        ctaLabel={t('contacts.logInteraction', 'Log Interaction')}
        onCtaClick={() => navigate(`/contacts/${contactId}/interaction`)}
      />
    )
  }

  return (
    <div className="space-y-3">
      {/* Filter chips */}
      <div className="-mx-1 flex scrollbar-none gap-1.5 overflow-x-auto px-1 pb-1">
        {TIMELINE_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => {
              setFilter(f.key)
              onPageChange(1)
            }}
            className={cn(
              'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors',
              filter === f.key
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest',
            )}
          >
            {t(f.labelKey, f.fallback)}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="text-on-surface-variant size-5 animate-spin" />
        </div>
      )}

      {/* Filtered empty state */}
      {!isLoading && items.length === 0 && filter !== 'all' && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-on-surface-variant text-sm">
            {t('timeline.noResults', 'No items match this filter')}
          </p>
        </div>
      )}

      {/* Timeline entries grouped by date */}
      {dateGroups.map((group) => (
        <div key={group.label}>
          <div className="sticky top-0 z-10 flex items-center gap-2 py-2">
            <div className="bg-outline-variant/30 h-px flex-1" />
            <span className="text-on-surface-variant bg-background rounded-full px-2.5 py-0.5 text-xs font-medium">
              {group.label}
            </span>
            <div className="bg-outline-variant/30 h-px flex-1" />
          </div>
          <div className="space-y-2">
            {group.entries.map((entry) => {
              switch (entry.kind) {
                case 'interaction':
                  return (
                    <InteractionCard
                      key={`i-${entry.id}`}
                      interaction={entry.data as Interaction}
                      currentContactId={contactId}
                      isExpanded={expandedId === entry.id}
                      onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      onEdit={onEditInteraction}
                      onDelete={onDeleteInteraction}
                      userRole={userRole}
                    />
                  )
                case 'note':
                  return <NoteCard key={`n-${entry.id}`} note={entry.data as ContactNote} />
                case 'voice':
                  return (
                    <VoiceNoteCard
                      key={`v-${entry.id}`}
                      voiceNote={entry.data as VoiceNote}
                      userRole={userRole}
                    />
                  )
                case 'todo':
                  return <TodoCard key={`t-${entry.id}`} todo={entry.data as ContactTodo} />
                case 'gift':
                  return <GiftCard key={`g-${entry.id}`} gift={entry.data as ContactGift} />
                case 'reminder':
                  return <ReminderCard key={`r-${entry.id}`} reminder={entry.data as Reminder} />
                case 'email':
                  return (
                    <EmailCard
                      key={`e-${entry.id}`}
                      email={entry.data as unknown as Record<string, unknown>}
                    />
                  )
                default:
                  return null
              }
            })}
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            {t('timeline.previous', 'Previous')}
          </Button>
          <span className="text-on-surface-variant text-xs">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            {t('timeline.next', 'Next')}
          </Button>
        </div>
      )}
    </div>
  )
}

/* -- Timeline card sub-components ----------------------------------------- */

function InteractionCard({
  interaction,
  currentContactId,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  userRole,
}: {
  interaction: Interaction
  currentContactId: string
  isExpanded: boolean
  onToggle: () => void
  onEdit: (i: Interaction) => void
  onDelete: (id: string) => void
  userRole: Role
}) {
  const Icon = typeIcons[interaction.type] ?? MoreHorizontal
  // Show other participants (exclude current contact being viewed)
  const otherParticipants = (interaction.participants ?? []).filter(
    (p) => p.id !== currentContactId,
  )

  return (
    <div className="bg-surface-container-low rounded-xl">
      <button
        type="button"
        className="flex w-full items-start gap-3 p-3 text-left"
        onClick={onToggle}
      >
        <div className="bg-surface-container-high flex size-8 shrink-0 items-center justify-center rounded-full">
          <Icon className="text-on-surface-variant size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">
              {interaction.title || interaction.type.replace('_', ' ')}
            </p>
            {interaction.quality && (
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                  qualityColors[interaction.quality] ?? qualityColors.neutral,
                )}
              >
                {interaction.quality}
              </span>
            )}
          </div>
          <p className="text-on-surface-variant text-xs">
            {format(new Date(interaction.occurredAt), 'MMM d, yyyy')}
          </p>
          {/* Participant avatars for group interactions */}
          {otherParticipants.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1">
              <span className="text-on-surface-variant text-xs">with</span>
              <div className="flex -space-x-1.5">
                {otherParticipants.slice(0, 5).map((p) => (
                  <Link
                    key={p.id}
                    to={`/contacts/${p.id}`}
                    onClick={(e) => e.stopPropagation()}
                    title={[p.firstName, p.lastName].filter(Boolean).join(' ')}
                    className="relative"
                  >
                    {p.avatarUrl ? (
                      <img
                        src={p.avatarUrl}
                        alt=""
                        className="border-card size-5 rounded-full border object-cover"
                      />
                    ) : (
                      <div className="bg-primary/10 text-primary border-card flex size-5 items-center justify-center rounded-full border text-[8px] font-semibold">
                        {p.firstName.charAt(0).toUpperCase()}
                        {p.lastName?.charAt(0).toUpperCase() ?? ''}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
              {otherParticipants.length > 5 && (
                <span className="text-on-surface-variant text-xs">
                  +{otherParticipants.length - 5}
                </span>
              )}
              <span className="text-on-surface-variant text-xs">
                {otherParticipants
                  .slice(0, 3)
                  .map((p) => p.firstName)
                  .join(', ')}
                {otherParticipants.length > 3 && ` +${otherParticipants.length - 3}`}
              </span>
            </div>
          )}
          {!isExpanded && interaction.notes && (
            <p className="text-on-surface-variant mt-1 line-clamp-2 text-sm">{interaction.notes}</p>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-outline-variant/20 border-t px-3 pt-2 pb-3">
          {interaction.notes && (
            <p className="text-on-surface-variant text-sm whitespace-pre-wrap">
              {interaction.notes}
            </p>
          )}
          {interaction.location && (
            <p className="text-on-surface-variant mt-2 flex items-center gap-1 text-xs">
              <MapPin className="size-3" />
              {interaction.location}
            </p>
          )}
          {interaction.meetingContext && (
            <div className="bg-surface-container-high/50 mt-2 rounded p-2">
              <p className="text-on-surface-variant text-xs font-medium">Meeting context</p>
              <p className="text-on-surface-variant mt-0.5 text-sm whitespace-pre-wrap">
                {interaction.meetingContext}
              </p>
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onEdit(interaction)
              }}
            >
              <Pencil className="mr-1 size-3" />
              Edit
            </Button>
            {canAccess('ai_summaries', userRole) && (
              <SummaryButton type="interaction" sourceId={interaction.id} />
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(interaction.id)
              }}
            >
              <Trash2 className="mr-1 size-3" />
              Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function NoteCard({ note }: { note: ContactNote }) {
  return (
    <div className="bg-surface-container-low rounded-xl">
      <div className="flex w-full items-start gap-3 p-3 text-left">
        <div className="bg-sand text-sand-foreground flex size-8 shrink-0 items-center justify-center rounded-full">
          <FileText className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Note</p>
          <p className="text-on-surface-variant text-xs">
            {format(new Date(note.occurredAt), 'MMM d, yyyy')}
          </p>
          <p className="text-on-surface-variant mt-1 line-clamp-3 text-sm whitespace-pre-wrap">
            {note.content}
          </p>
        </div>
      </div>
    </div>
  )
}

function VoiceNoteCard({ voiceNote, userRole }: { voiceNote: VoiceNote; userRole: Role }) {
  return (
    <div className="bg-surface-container-low rounded-xl">
      <div className="flex w-full items-start gap-3 p-3 text-left">
        <div className="bg-coral-soft text-coral flex size-8 shrink-0 items-center justify-center rounded-full">
          <Mic className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">Voice Note</p>
            {voiceNote.durationSeconds != null && (
              <span className="text-on-surface-variant text-xs">
                {Math.floor(voiceNote.durationSeconds / 60)}:
                {String(voiceNote.durationSeconds % 60).padStart(2, '0')}
              </span>
            )}
          </div>
          <p className="text-on-surface-variant text-xs">
            {format(new Date(voiceNote.createdAt), 'MMM d, yyyy')}
          </p>
          {voiceNote.status === 'transcribing' && (
            <p className="text-on-surface-variant mt-1 text-sm italic">Transcribing...</p>
          )}
          {voiceNote.transcript && (
            <p className="text-on-surface-variant mt-1 line-clamp-3 text-sm whitespace-pre-wrap">
              {voiceNote.transcript}
            </p>
          )}
          {voiceNote.status === 'failed' && (
            <p className="text-destructive mt-1 text-sm">Transcription failed</p>
          )}
          {voiceNote.transcript && canAccess('ai_summaries', userRole) && (
            <div className="mt-2">
              <SummaryButton type="voice_note" sourceId={voiceNote.id} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TodoCard({ todo }: { todo: ContactTodo }) {
  return (
    <div className="bg-surface-container-low rounded-xl">
      <div className="flex w-full items-start gap-3 p-3 text-left">
        <div className="bg-chartreuse-soft text-chartreuse-foreground dark:text-chartreuse flex size-8 shrink-0 items-center justify-center rounded-full">
          <CheckCircle2 className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium line-through opacity-70">{todo.title}</p>
          <p className="text-on-surface-variant text-xs">
            Completed {format(new Date(todo.completedAt!), 'MMM d, yyyy')}
          </p>
        </div>
      </div>
    </div>
  )
}

function GiftCard({ gift }: { gift: ContactGift }) {
  return (
    <div className="bg-surface-container-low rounded-xl">
      <div className="flex w-full items-start gap-3 p-3 text-left">
        <div className="bg-coral-soft text-coral flex size-8 shrink-0 items-center justify-center rounded-full">
          <Gift className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">{gift.title}</p>
            {gift.price && <span className="text-on-surface-variant text-xs">${gift.price}</span>}
          </div>
          <p className="text-on-surface-variant text-xs">
            Purchased · {format(new Date(gift.createdAt), 'MMM d, yyyy')}
          </p>
        </div>
      </div>
    </div>
  )
}

function ReminderCard({ reminder }: { reminder: Reminder }) {
  return (
    <div className="bg-surface-container-low rounded-xl">
      <div className="flex w-full items-start gap-3 p-3 text-left">
        <div className="bg-lilac-soft text-lilac-foreground dark:text-lilac flex size-8 shrink-0 items-center justify-center rounded-full">
          <Bell className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">{reminder.title}</p>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                reminder.sent ? 'bg-muted text-muted-foreground' : 'tag-lilac',
              )}
            >
              {reminder.type}
            </span>
          </div>
          <p className="text-on-surface-variant text-xs">
            {format(new Date(reminder.remindAt), 'MMM d, yyyy')}
            {reminder.recurring !== 'none' && ` · ${reminder.recurring}`}
          </p>
        </div>
      </div>
    </div>
  )
}

function EmailCard({ email }: { email: Record<string, unknown> }) {
  const direction = email.direction as string
  const subject = (email.subject as string) || '(no subject)'
  const sentAt = email.sentAt as string
  const snippet = email.snippet as string | null

  return (
    <div className="bg-surface-container-low rounded-xl">
      <div className="flex w-full items-start gap-3 p-3 text-left">
        <div
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full',
            direction === 'sent'
              ? 'bg-coral-soft text-coral'
              : 'bg-lilac-soft text-lilac-foreground dark:text-lilac',
          )}
        >
          <Mail className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">{subject}</p>
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                direction === 'sent' ? 'tag-coral' : 'tag-lilac',
              )}
            >
              {direction === 'sent' ? 'Sent' : 'Received'}
            </span>
          </div>
          {snippet && (
            <p className="text-on-surface-variant mt-0.5 line-clamp-2 text-xs">{snippet}</p>
          )}
          <p className="text-on-surface-variant mt-1 text-xs">
            {format(new Date(sentAt), 'MMM d, yyyy · h:mm a')}
          </p>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Notes Tab                                                                 */
/* -------------------------------------------------------------------------- */

function NotesTab({
  notes,
  total,
  page,
  onPageChange,
  search,
  onSearchChange,
  onCreate,
  isCreating,
  onUpdate,
  onDelete,
}: {
  notes: ContactNote[]
  total: number
  page: number
  onPageChange: (p: number) => void
  search: string
  onSearchChange: (v: string) => void
  onCreate: (content: string) => void
  isCreating: boolean
  onUpdate: (id: string, content: string) => void
  onDelete: (id: string) => void
}) {
  const [newContent, setNewContent] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')

  const pageSize = 20
  const totalPages = Math.ceil(total / pageSize)

  function handleCreate(e: FormEvent) {
    e.preventDefault()
    const content = newContent.trim()
    if (!content) return
    onCreate(content)
    setNewContent('')
  }

  function startEdit(note: ContactNote) {
    setEditingId(note.id)
    setEditContent(note.content)
  }

  function saveEdit() {
    if (!editingId || !editContent.trim()) return
    onUpdate(editingId, editContent.trim())
    setEditingId(null)
    setEditContent('')
  }

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="text-on-surface-variant absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search notes..."
          value={search}
          onChange={(e) => {
            onSearchChange(e.target.value)
            onPageChange(1)
          }}
          className="bg-background placeholder:text-on-surface-variant focus:border-ring focus:ring-ring w-full rounded-lg border py-2 pr-3 pl-9 text-sm transition-colors outline-none focus:ring-1"
        />
      </div>

      {/* Add note */}
      <form onSubmit={handleCreate} className="space-y-2">
        <textarea
          placeholder="Add a note..."
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          rows={2}
          className="bg-background placeholder:text-on-surface-variant focus:border-ring focus:ring-ring w-full resize-none rounded-lg border px-3 py-2 text-sm transition-colors outline-none focus:ring-1"
        />
        {newContent.trim() && (
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={isCreating}>
              {isCreating ? 'Saving...' : 'Add Note'}
            </Button>
          </div>
        )}
      </form>

      {/* Note list */}
      {notes.length === 0 && !search && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <FileText className="text-on-surface-variant/50 mb-2 size-8" />
          <p className="text-on-surface-variant text-sm">No notes yet</p>
        </div>
      )}

      {notes.length === 0 && search && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Search className="text-on-surface-variant/50 mb-2 size-8" />
          <p className="text-on-surface-variant text-sm">No matching notes</p>
        </div>
      )}

      {notes.map((note) => (
        <div key={note.id} className="bg-surface-container-low rounded-xl p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-on-surface-variant text-xs">
                {format(new Date(note.occurredAt), 'MMM d, yyyy h:mm a')}
              </p>
              {editingId === note.id ? (
                <div className="mt-1 space-y-2">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    className="bg-background focus:border-ring focus:ring-ring w-full resize-none rounded-lg border px-3 py-2 text-sm transition-colors outline-none focus:ring-1"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-sm whitespace-pre-wrap">{note.content}</p>
              )}
            </div>
            {editingId !== note.id && (
              <div className="flex shrink-0 gap-1">
                <button
                  onClick={() => startEdit(note)}
                  className="text-on-surface-variant hover:text-foreground transition-colors"
                >
                  <Pencil className="size-3.5" />
                </button>
                <button
                  onClick={() => onDelete(note.id)}
                  className="text-on-surface-variant hover:text-destructive transition-colors"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </Button>
          <span className="text-on-surface-variant text-xs">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Todos Tab                                                                 */
/* -------------------------------------------------------------------------- */

function TodosTab({
  todos,
  newTitle,
  onNewTitleChange,
  onAdd,
  isAdding,
  onToggle,
  onDelete,
}: {
  todos: ContactTodo[]
  newTitle: string
  onNewTitleChange: (v: string) => void
  onAdd: (e: FormEvent) => void
  isAdding: boolean
  onToggle: (todo: ContactTodo) => void
  onDelete: (id: string) => void
}) {
  const sorted = [...todos].sort((a, b) => {
    if (a.completed !== b.completed) return a.completed ? 1 : -1
    return a.sortOrder - b.sortOrder
  })

  return (
    <div className="space-y-3">
      <form onSubmit={onAdd} className="flex gap-2">
        <input
          type="text"
          placeholder="Add a todo..."
          value={newTitle}
          onChange={(e) => onNewTitleChange(e.target.value)}
          className="bg-background placeholder:text-on-surface-variant focus:border-ring focus:ring-ring flex-1 rounded-lg border px-3 py-2 text-sm transition-colors outline-none focus:ring-1"
        />
        <Button type="submit" size="sm" disabled={!newTitle.trim() || isAdding}>
          Add
        </Button>
      </form>

      {sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CheckSquare className="text-on-surface-variant/50 mb-2 size-8" />
          <p className="text-on-surface-variant text-sm">No todos yet</p>
        </div>
      )}

      {sorted.map((todo) => (
        <div
          key={todo.id}
          className="bg-surface-container-low flex items-start gap-3 rounded-xl p-3"
        >
          <button
            onClick={() => onToggle(todo)}
            className="text-on-surface-variant hover:text-foreground mt-0.5 shrink-0 transition-colors"
          >
            {todo.completed ? (
              <CheckSquare className="text-primary size-4" />
            ) : (
              <Square className="size-4" />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <p className={cn('text-sm', todo.completed && 'text-on-surface-variant line-through')}>
              {todo.title}
            </p>
            {todo.dueDate && (
              <p className="text-on-surface-variant flex items-center gap-1 text-xs">
                <CalendarDays className="size-3" />
                {format(new Date(todo.dueDate), 'MMM d, yyyy')}
              </p>
            )}
          </div>
          <button
            onClick={() => onDelete(todo.id)}
            className="text-on-surface-variant hover:text-destructive shrink-0 transition-colors"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Gifts Tab                                                                 */
/* -------------------------------------------------------------------------- */

function GiftsTab({
  gifts,
  newTitle,
  newPrice,
  onNewTitleChange,
  onNewPriceChange,
  onAdd,
  isAdding,
  onTogglePurchased,
  onDelete,
}: {
  gifts: ContactGift[]
  newTitle: string
  newPrice: string
  onNewTitleChange: (v: string) => void
  onNewPriceChange: (v: string) => void
  onAdd: (e: FormEvent) => void
  isAdding: boolean
  onTogglePurchased: (gift: ContactGift) => void
  onDelete: (id: string) => void
}) {
  const { t } = useTranslation()
  const sorted = [...gifts].sort((a, b) => {
    if (a.purchased !== b.purchased) return a.purchased ? 1 : -1
    return a.sortOrder - b.sortOrder
  })

  return (
    <div className="space-y-3">
      <form onSubmit={onAdd} className="flex gap-2">
        <input
          type="text"
          placeholder="Gift idea..."
          value={newTitle}
          onChange={(e) => onNewTitleChange(e.target.value)}
          className="bg-background placeholder:text-on-surface-variant focus:border-ring focus:ring-ring min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm transition-colors outline-none focus:ring-1"
        />
        <input
          type="text"
          placeholder="Price"
          value={newPrice}
          onChange={(e) => onNewPriceChange(e.target.value)}
          className="bg-background placeholder:text-on-surface-variant focus:border-ring focus:ring-ring w-20 rounded-lg border px-3 py-2 text-sm transition-colors outline-none focus:ring-1"
        />
        <Button type="submit" size="sm" disabled={!newTitle.trim() || isAdding}>
          Add
        </Button>
      </form>

      {sorted.length === 0 && (
        <EmptyState
          icon={Gift}
          title={t('contacts.noGifts', 'No gift ideas yet')}
          description={t('contacts.noGiftsHint', 'Add a gift idea using the form above.')}
        />
      )}

      {sorted.map((gift) => (
        <div
          key={gift.id}
          className="bg-surface-container-low flex items-start gap-3 rounded-xl p-3"
        >
          <button
            onClick={() => onTogglePurchased(gift)}
            className="text-on-surface-variant hover:text-foreground mt-0.5 shrink-0 transition-colors"
            title={gift.purchased ? 'Mark as not purchased' : 'Mark as purchased'}
          >
            {gift.purchased ? (
              <ShoppingCart className="text-primary size-4" />
            ) : (
              <Gift className="size-4" />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'text-sm font-medium',
                gift.purchased && 'text-on-surface-variant line-through',
              )}
            >
              {gift.title}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {gift.price && <span className="text-on-surface-variant text-xs">${gift.price}</span>}
              {gift.url && (
                <a
                  href={gift.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary inline-flex items-center gap-0.5 text-xs hover:underline"
                >
                  Link <ExternalLink className="size-3" />
                </a>
              )}
            </div>
            {gift.notes && <p className="text-on-surface-variant mt-0.5 text-xs">{gift.notes}</p>}
          </div>
          <button
            onClick={() => onDelete(gift.id)}
            className="text-on-surface-variant hover:text-destructive shrink-0 transition-colors"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Voice Notes Tab                                                           */
/* -------------------------------------------------------------------------- */

function VoiceNotesTab({
  contactId,
  voiceNotes,
  onSaved,
  onDelete,
  userRole,
}: {
  contactId: string
  voiceNotes: VoiceNote[]
  onSaved: () => void
  onDelete: (id: string) => void
  userRole: Role
}) {
  const [showRecorder, setShowRecorder] = useState(false)

  return (
    <div className="space-y-3">
      {!showRecorder && (
        <Button size="sm" onClick={() => setShowRecorder(true)}>
          <Plus className="mr-1 size-4" />
          Record
        </Button>
      )}

      {showRecorder && (
        <VoiceNoteRecorder
          contactId={contactId}
          onSaved={() => {
            setShowRecorder(false)
            onSaved()
          }}
          onCancel={() => setShowRecorder(false)}
        />
      )}

      {voiceNotes.length === 0 && !showRecorder && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <MessageSquare className="text-on-surface-variant/50 mb-2 size-8" />
          <p className="text-on-surface-variant text-sm">No voice notes yet</p>
        </div>
      )}

      {voiceNotes.map((note) => (
        <div key={note.id} className="bg-surface-container-low rounded-xl p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-on-surface-variant text-xs">
                {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                {note.durationSeconds != null &&
                  ` · ${Math.floor(note.durationSeconds / 60)}:${String(note.durationSeconds % 60).padStart(2, '0')}`}
              </p>
              {note.status === 'transcribing' && (
                <p className="text-on-surface-variant mt-1 text-sm italic">Transcribing...</p>
              )}
              {note.transcript && (
                <p className="mt-1 text-sm whitespace-pre-wrap">{note.transcript}</p>
              )}
              {note.status === 'failed' && (
                <p className="text-destructive mt-1 text-sm">Transcription failed</p>
              )}
            </div>
            <button
              onClick={() => onDelete(note.id)}
              className="text-on-surface-variant hover:text-destructive shrink-0 transition-colors"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
          {note.transcript && canAccess('ai_summaries', userRole) && (
            <div className="mt-2 flex">
              <SummaryButton type="voice_note" sourceId={note.id} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
